import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiListMvrCarrierRules } from 'api';
import type {
  MVRCarrierRule,
  MVRCarrierRulesFilters,
  MVRProductType,
  MVRRequestTransactionType,
} from 'api';
import type { SelectOption } from 'components/fields/SelectSearch';
import { resolveProductTypeReadiness } from './resolveProductTypeReadiness';

interface UseMvrCarriersForProvinceParams {
  provinceId: number | null;
  transactionType: MVRRequestTransactionType;
  productType: MVRProductType | null;
  requiresProductType: boolean;
}

interface UseMvrCarriersForProvinceReturn {
  carrierOptions: SelectOption[];
  carrierRules: MVRCarrierRule[];
  isLoadingCarrierOptions: boolean;
  carrierOptionsError: unknown;
}

const MAX_CARRIER_RULE_PAGES = 10;

const fetchAllCarrierRulesForProvince = async (
  filters: MVRCarrierRulesFilters,
): Promise<MVRCarrierRule[]> => {
  let page = 1;
  let results: MVRCarrierRule[] = [];
  while (page <= MAX_CARRIER_RULE_PAGES) {
    const response = await apiListMvrCarrierRules(filters, page);
    results = results.concat(response.results);
    if (!response.next) return results;
    page += 1;
  }
  return results;
};

export const useMvrCarriersForProvince = ({
  provinceId,
  transactionType,
  productType,
  requiresProductType,
}: UseMvrCarriersForProvinceParams): UseMvrCarriersForProvinceReturn => {
  const { productTypeParam, isProductTypeReady } = resolveProductTypeReadiness(
    productType,
    requiresProductType,
  );
  const enabled =
    provinceId !== null && !!transactionType && isProductTypeReady;

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'mvr-carriers-for-province',
      provinceId,
      transactionType,
      productTypeParam ?? null,
    ],
    queryFn: () =>
      fetchAllCarrierRulesForProvince({
        province: provinceId!,
        transaction_type: transactionType,
        ...(productTypeParam ? { product_type: productTypeParam } : {}),
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const carrierRules = data ?? [];

  const carrierOptions = useMemo<SelectOption[]>(
    () =>
      carrierRules.map((row) => ({
        value: row.carrier_id,
        label: row.carrier_name,
      })),
    [carrierRules],
  );

  return {
    carrierOptions,
    carrierRules,
    isLoadingCarrierOptions: isLoading,
    carrierOptionsError: error,
  };
};
